import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  Dimensions,
  Platform,
  TextInput,
  ActivityIndicator,
  Modal,
  Pressable,
  Linking,
  useWindowDimensions,
} from 'react-native';
import { useRoute, useNavigation, RouteProp, useFocusEffect } from '@react-navigation/native';
import { formatPct } from '../../utils/formatPercentage';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle, G, Text as SvgText, Line, Defs, LinearGradient, Stop } from 'react-native-svg';
import { kpisApi, kpiValuesApi, kpiCommentsApi, evidenceApi, Evidence, KPIComment as ApiKPIComment } from '../../services/apiService';
import { exportKPIReadingsToExcel } from '../../utils/excelExport';
import { KPI, KPIValue } from '../../types';
import ConfirmModal from '../../components/ConfirmModal';
import { COLORS, FONTS, API_BASE_HOST } from '../../constants/config';
import { LoadingScreen, EmptyState, PageContainer, Navbar, ViewOnlyBadge, Breadcrumb } from '../../components';
import TargetAchievementCard from '../../components/TargetAchievementCard';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useResponsive, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { useCachedData } from '../../hooks/useCachedData';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

type RouteProps = RouteProp<MainStackParamList, 'KPIDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

const { width: screenWidth } = Dimensions.get('window');

// ============================================================
// SVG Icons
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ExportIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M17 8L12 3M12 3L7 8M12 3V15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CommentIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EllipsisIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="5" r="1.5" fill={color} />
    <Circle cx="12" cy="12" r="1.5" fill={color} />
    <Circle cx="12" cy="19" r="1.5" fill={color} />
  </Svg>
);

const ChevronRightIcon = ({ size = 14, color = '#334155' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MessageCirclePlusIcon = ({ size = 20, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M7.9 20A9 9 0 1 0 4 16.1L2 22l5.9-2z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 12h8M12 8v8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EllipsisVerticalIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="12" r="1" fill={color} />
    <Circle cx="12" cy="5" r="1" fill={color} />
    <Circle cx="12" cy="19" r="1" fill={color} />
  </Svg>
);

const CheckIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const XIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 16, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#EF4444' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrendingUpIcon = ({ size = 16, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M23 6L13.5 15.5L8.5 10.5L1 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 6H23V12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrendingDownIcon = ({ size = 16, color = '#EF4444' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M23 18L13.5 8.5L8.5 13.5L1 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 18H23V12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChartIcon = ({ size = 48, color = '#9CA3AF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 3V21H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 16L12 11L16 15L21 10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Donut Chart Component (Enhanced with Achievement Status)
// ============================================================

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  showStatus?: boolean;
  showLabel?: boolean;
}

// Get color based on achievement percentage
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return '#22C55E'; // Excellent - Green
  if (percentage >= 80) return '#84CC16';  // Good - Lime
  if (percentage >= 60) return '#EAB308';  // Acceptable - Yellow
  return '#EF4444';                         // Poor - Red
};

// Get status label - returns translation key
const getAchievementLabelKey = (percentage: number): string => {
  if (percentage >= 100) return 'kpis.excellent';
  if (percentage >= 80) return 'kpis.good';
  if (percentage >= 60) return 'kpis.acceptable';
  return 'kpis.poor';
};

const DonutChart = ({ percentage, size = 140, strokeWidth = 20, showStatus = true, showLabel = true }: DonutChartProps) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // Cap visual display at 100% while showing actual value in text
  const displayPercentage = Math.min(percentage, 100);
  const strokeDashoffset = circumference - (displayPercentage / 100) * circumference;
  const center = size / 2;

  // Get color based on achievement status
  const progressColor = getAchievementColor(percentage);
  const statusLabel = t(getAchievementLabelKey(percentage));

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((displayPercentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  // Theme colors
  const bgCircleColor = isDark ? '#374151' : '#E5E7EB';
  const dotStrokeColor = isDark ? colors.card : '#FCFCFC';

  return (
    <View style={{ alignItems: 'center' }}>
      <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
        <Svg width={size} height={size}>
          {/* Background circle */}
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={bgCircleColor}
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Progress arc - rotation applied to this element only */}
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={progressColor}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
          {/* Dot at end of progress - ONLY show if percentage > 0 */}
          {displayPercentage > 0 && (
            <Circle
              cx={dotX}
              cy={dotY}
              r={strokeWidth / 2 + 2}
              fill={progressColor}
              stroke={dotStrokeColor}
              strokeWidth={2}
            />
          )}
        </Svg>
        <View style={{ position: 'absolute', alignItems: 'center' }}>
          <Text style={{ fontSize: size * 0.2, fontFamily: FONTS.numbers, fontWeight: 'bold', color: colors.textPrimary }}>
            {percentage} %
          </Text>
          {showLabel && (
            <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: colors.textMuted }}>
              {t('kpis.achievementPercentage')}
            </Text>
          )}
        </View>
      </View>
      {/* Status Badge */}
      {showStatus && (
        <View style={{
          marginTop: 12,
          paddingHorizontal: 16,
          paddingVertical: 6,
          borderRadius: 16,
          backgroundColor: `${progressColor}20`,
        }}>
          <Text style={{
            fontSize: 13,
            fontFamily: FONTS.medium,
            color: progressColor,
          }}>
            {statusLabel}
          </Text>
        </View>
      )}
    </View>
  );
};

// ============================================================
// Mini Donut Chart Component (for table cells)
// ============================================================

interface MiniDonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

const MiniDonutChart = ({ percentage, size = 28, strokeWidth = 4 }: MiniDonutChartProps) => {
  const { isDark } = useTheme();
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  // Cap visual display at 100% while showing actual value
  const displayPercentage = Math.min(percentage, 100);
  const strokeDashoffset = circumference - (displayPercentage / 100) * circumference;
  const center = size / 2;

  // Use achievement-based colors
  const progressColor = getAchievementColor(percentage);
  const bgCircleColor = isDark ? '#374151' : '#E5E7EB';

  return (
    <Svg width={size} height={size}>
      {/* Background circle */}
      <Circle
        cx={center}
        cy={center}
        r={radius}
        stroke={bgCircleColor}
        strokeWidth={strokeWidth}
        fill="none"
      />
      {/* Progress circle */}
      <Circle
        cx={center}
        cy={center}
        r={radius}
        stroke={progressColor}
        strokeWidth={strokeWidth}
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${center} ${center})`}
      />
    </Svg>
  );
};

// ============================================================
// KPI Comment Helpers
// ============================================================

// Helper to format date for display
const formatCommentDate = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleDateString('ar-AE', options);
  } catch {
    return dateStr;
  }
};

// ============================================================
// Comment Data Interface
// ============================================================

interface CommentData {
  id: number;
  name: string;
  initials: string;
  date: string;
  text: string;
  avatarColor: string;
  showMenu: boolean;
  isEdited?: boolean;
  originalText?: string;
  userType?: 'strategy_team' | 'org_employee' | 'superuser' | 'executive';
}

// ============================================================
// Comment Card Component (matches GoalDetailScreen design)
// ============================================================

interface CommentCardProps {
  comment: CommentData;
  onEdit?: (comment: CommentData) => void;
  onDelete?: (id: number) => void;
  isOrgEmployee?: boolean;
}

const CommentCard = ({ comment, onEdit, onDelete, isOrgEmployee = false }: CommentCardProps) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const [menuVisible, setMenuVisible] = useState(false);
  const [showOriginalPopup, setShowOriginalPopup] = useState(false);

  // Theme colors for comment card
  const commentTheme = {
    cardBg: isDark ? colors.backgroundAlt : '#F1F5F9',
    menuDropdownBg: colors.card,
    menuDropdownBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    separatorColor: colors.cardBorder,
    modalBg: colors.card,
    modalBorder: colors.cardBorder,
  };

  const handleEdit = () => {
    setMenuVisible(false);
    onEdit?.(comment);
  };

  const handleDelete = () => {
    setMenuVisible(false);
    onDelete?.(comment.id);
  };

  // Get user type label and badge color
  const getUserTypeBadge = () => {
    switch (comment.userType) {
      case 'strategy_team':
        return { label: t('users.strategyTeam'), color: '#A6813F', bgColor: '#FEF3C7' };
      case 'org_employee':
        return { label: t('comments.orgEmployee'), color: '#9467BD', bgColor: '#F3E8FF' };
      case 'superuser':
        return { label: t('users.systemAdmin'), color: '#2563EB', bgColor: '#DBEAFE' };
      case 'executive':
        return { label: t('users.executive'), color: '#059669', bgColor: '#D1FAE5' };
      default:
        return null;
    }
  };

  const userTypeBadge = getUserTypeBadge();

  return (
    <View style={[styles.commentCard, { backgroundColor: commentTheme.cardBg }, menuVisible && styles.commentCardMenuOpen]}>
      {/* Header row with avatar, name/date, and optional menu */}
      <View style={styles.commentHeader}>
        {/* Menu icon (optional) */}
        {comment.showMenu && (
          <View style={styles.commentMenuWrapper}>
            <TouchableOpacity
              style={styles.commentMenuButton}
              onPress={() => setMenuVisible(!menuVisible)}
            >
              <EllipsisVerticalIcon size={16} color={commentTheme.textPrimary} />
            </TouchableOpacity>

            {/* Dropdown Menu */}
            {menuVisible && (
              <View style={[styles.commentMenuDropdown, { backgroundColor: commentTheme.menuDropdownBg, borderColor: commentTheme.menuDropdownBorder }]}>
                <TouchableOpacity style={styles.commentMenuItem} onPress={handleEdit}>
                  <Text style={[styles.commentMenuItemText, { color: commentTheme.textPrimary }]}>{t('common.edit')}</Text>
                  <EditIcon size={16} color={commentTheme.textPrimary} />
                </TouchableOpacity>
                <View style={[styles.commentMenuSeparator, { backgroundColor: commentTheme.separatorColor }]} />
                <TouchableOpacity style={styles.commentMenuItem} onPress={handleDelete}>
                  <Text style={styles.commentMenuItemTextDelete}>{t('common.delete')}</Text>
                  <TrashIcon size={16} color="#EF4444" />
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}

        {/* Name and date */}
        <View style={styles.commentAuthorInfo}>
          {/* Name on top */}
          <Text style={[styles.commentAuthorName, { color: commentTheme.textPrimary }]}>{comment.name}</Text>
          {/* User type badge and edited tag below name */}
          <View style={styles.commentBadgeRow}>
            {/* Edited tag */}
            {comment.isEdited && (
              <TouchableOpacity
                style={styles.editedTag}
                onPress={() => setShowOriginalPopup(true)}
              >
                <Text style={styles.editedTagText}>{t('comments.edited')}</Text>
              </TouchableOpacity>
            )}
            {/* User type badge */}
            {userTypeBadge && (
              <View style={[styles.userTypeBadge, { backgroundColor: userTypeBadge.bgColor }]}>
                <Text style={[styles.userTypeBadgeText, { color: userTypeBadge.color }]}>
                  {userTypeBadge.label}
                </Text>
              </View>
            )}
          </View>
          <Text style={[styles.commentDate, { color: commentTheme.textMuted }]}>{comment.date}</Text>
        </View>

        {/* Avatar */}
        <View style={[styles.commentAvatar, { backgroundColor: comment.avatarColor }]}>
          <Text style={styles.commentAvatarText}>{comment.initials}</Text>
        </View>
      </View>

      {/* Separator line */}
      <View style={[styles.commentSeparatorLine, { backgroundColor: commentTheme.separatorColor }]} />

      {/* Comment text */}
      <View style={styles.commentContent}>
        <Text style={[styles.commentText, { color: commentTheme.textPrimary }]}>{comment.text}</Text>
      </View>

      {/* Original Text Popup Modal */}
      <Modal
        visible={showOriginalPopup}
        transparent
        animationType="fade"
        onRequestClose={() => setShowOriginalPopup(false)}
      >
        <Pressable
          style={styles.originalTextModalOverlay}
          onPress={() => setShowOriginalPopup(false)}
        >
          <View style={[styles.originalTextModalContent, { backgroundColor: commentTheme.modalBg }]}>
            <View style={[styles.originalTextModalHeader, { borderBottomColor: commentTheme.separatorColor }]}>
              <TouchableOpacity onPress={() => setShowOriginalPopup(false)}>
                <XIcon size={18} color={commentTheme.textPrimary} />
              </TouchableOpacity>
              <Text style={[styles.originalTextModalTitle, { color: commentTheme.textPrimary }]}>{t('comments.originalComment')}</Text>
            </View>
            <View style={styles.originalTextModalBody}>
              <Text style={[styles.originalTextModalText, { color: commentTheme.textPrimary }]}>
                {comment.originalText || t('comments.noOriginalText')}
              </Text>
            </View>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

// ============================================================
// Add Comment Input Component (matches GoalDetailScreen design)
// ============================================================

interface AddCommentInputProps {
  onSubmit: (text: string) => void;
  onCancel: () => void;
  editingComment?: CommentData | null;
}

const AddCommentInput = ({ onSubmit, onCancel, editingComment }: AddCommentInputProps) => {
  const [text, setText] = useState('');
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();

  const commentCardTheme = {
    background: isDark ? colors.backgroundAlt : '#F1F5F9',
    text: colors.textPrimary,
    inputBg: isDark ? colors.card : '#FFFFFF',
    inputText: colors.textPrimary,
    placeholder: colors.textMuted,
  };

  // Initialize text when editingComment changes
  useEffect(() => {
    if (editingComment) {
      setText(editingComment.text);
    } else {
      setText('');
    }
  }, [editingComment]);

  const handleSubmit = () => {
    if (text.trim()) {
      onSubmit(text.trim());
      setText('');
    }
  };

  const handleCancel = () => {
    setText('');
    onCancel();
  };

  return (
    <View style={[styles.addCommentCard, { backgroundColor: commentCardTheme.background }]}>
      {/* Header */}
      <View style={styles.addCommentHeader}>
        <Text style={[styles.addCommentHeaderText, { color: commentCardTheme.text }]}>
          {editingComment ? t('comments.editComment') : t('comments.addComment')}
        </Text>
      </View>

      {/* Separator */}
      <View style={styles.addCommentSeparator} />

      {/* Input area */}
      <View style={[styles.addCommentInputContainer, { backgroundColor: commentCardTheme.inputBg }]}>
        <TextInput
          style={[styles.addCommentInput, { color: commentCardTheme.inputText }]}
          value={text}
          onChangeText={setText}
          placeholder={t('comments.placeholder')}
          placeholderTextColor={commentCardTheme.placeholder}
          multiline
          textAlign="right"
        />
      </View>

      {/* Separator */}
      <View style={styles.addCommentSeparator} />

      {/* Action buttons */}
      <View style={styles.addCommentActions}>
        <TouchableOpacity style={styles.addCommentActionButton} onPress={handleCancel}>
          <XIcon size={16} color={commentCardTheme.text} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.addCommentActionButton} onPress={handleSubmit}>
          <CheckIcon size={16} color={commentCardTheme.text} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

// ============================================================
// Arabic Date Formatter
// ============================================================

const formatArabicDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const monthKeys = ['january', 'february', 'march', 'april', 'may', 'june',
                       'july', 'august', 'september', 'october', 'november', 'december'];
    const month = i18n.t(`common.months.${monthKeys[date.getMonth()]}`);
    return `${date.getDate()} ${month}`;
  } catch {
    return dateString;
  }
};

// ============================================================
// Enhanced Line Chart Component
// ============================================================

interface LineChartProps {
  data: { date: string; value: number; target: number }[];
  width?: number;
  height?: number;
  targetValue?: number;
  isPercentage?: boolean;
}

const EnhancedLineChart = ({ data, width: propWidth, height = 280, targetValue, isPercentage = true }: LineChartProps) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();
  const { width: screenWidth } = useWindowDimensions();

  // Responsive width - use prop if provided, otherwise calculate based on screen
  const width = propWidth || Math.min(screenWidth - 64, 600);

  // Theme colors for chart
  const chartTheme = {
    gridLine: isDark ? '#374151' : '#E5E7EB',
    axisLabel: colors.textMuted,
    legendText: colors.textMuted,
    actualLine: '#A9211F', // Brand primary color
    targetLine: '#22C55E', // Success green
    cardBackground: colors.card,
    dotStroke: isDark ? colors.card : '#FFFFFF',
  };

  const padding = { top: 30, right: 30, bottom: 50, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const values = data.map(d => d.value);
  const targets = data.map(d => d.target);
  const allValues = [...values, ...targets];
  const maxValue = Math.max(...allValues, 1) * 1.1; // Add 10% padding
  const minValue = Math.min(0, Math.min(...allValues) * 0.9); // Start from 0 or lower
  const valueRange = maxValue - minValue || 1;

  const getX = (index: number) => padding.left + (index / (data.length - 1 || 1)) * chartWidth;
  const getY = (value: number) => padding.top + chartHeight - ((value - minValue) / valueRange) * chartHeight;

  // Create path for actual values
  const actualPath = data.map((d, i) => {
    const x = getX(i);
    const y = getY(d.value);
    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).join(' ');

  // Create area path for gradient fill
  const areaPath = actualPath +
    ` L ${getX(data.length - 1)} ${padding.top + chartHeight}` +
    ` L ${padding.left} ${padding.top + chartHeight} Z`;

  // Target reference line Y position
  const targetY = targetValue !== undefined ? getY(targetValue) : null;

  // Generate grid lines (5 horizontal lines)
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(ratio => {
    const y = padding.top + chartHeight * (1 - ratio);
    const value = Math.round(minValue + valueRange * ratio);
    return { y, value };
  });

  return (
    <View style={{ width, height: height + 20 }}>
      <Svg width={width} height={height}>
        {/* Gradient definition for area fill */}
        <Defs>
          <LinearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0%" stopColor={chartTheme.actualLine} stopOpacity={0.3} />
            <Stop offset="100%" stopColor={chartTheme.actualLine} stopOpacity={0.05} />
          </LinearGradient>
        </Defs>

        {/* Horizontal Grid lines only */}
        {gridLines.map((line, i) => (
          <G key={i}>
            <Line
              x1={padding.left}
              y1={line.y}
              x2={width - padding.right}
              y2={line.y}
              stroke={chartTheme.gridLine}
              strokeWidth={1}
              strokeDasharray="4,4"
              opacity={0.5}
            />
            {/* Y-axis labels */}
            <SvgText
              x={padding.left - 12}
              y={line.y + 4}
              fontSize={11}
              fill={chartTheme.axisLabel}
              textAnchor="end"
              fontFamily="JF Flat"
            >
              {isPercentage ? `%${line.value}` : line.value}
            </SvgText>
          </G>
        ))}

        {/* Area fill under the line */}
        <Path
          d={areaPath}
          fill="url(#areaGradient)"
        />

        {/* Target Reference Line (dashed green) */}
        {targetY !== null && (
          <G>
            <Line
              x1={padding.left}
              y1={targetY}
              x2={width - padding.right}
              y2={targetY}
              stroke={chartTheme.targetLine}
              strokeWidth={2}
              strokeDasharray="8,4"
            />
            {/* Target label */}
            <SvgText
              x={width - padding.right + 8}
              y={targetY + 4}
              fontSize={10}
              fill={chartTheme.targetLine}
              textAnchor="start"
              fontFamily="JF Flat"
            >
              {isPercentage ? `${t('kpis.targetValue')}: %${targetValue}` : `${t('kpis.targetValue')}: ${targetValue}`}
            </SvgText>
          </G>
        )}

        {/* Actual values line */}
        <Path
          d={actualPath}
          stroke={chartTheme.actualLine}
          strokeWidth={3}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points with outer ring */}
        {data.map((d, i) => (
          <G key={i}>
            {/* Outer ring */}
            <Circle
              cx={getX(i)}
              cy={getY(d.value)}
              r={7}
              fill={chartTheme.dotStroke}
              stroke={chartTheme.actualLine}
              strokeWidth={2}
            />
            {/* Inner dot */}
            <Circle
              cx={getX(i)}
              cy={getY(d.value)}
              r={4}
              fill={chartTheme.actualLine}
            />
          </G>
        ))}

        {/* X-axis labels (dates) */}
        {data.map((d, i) => (
          <SvgText
            key={i}
            x={getX(i)}
            y={height - 15}
            fontSize={10}
            fill={chartTheme.axisLabel}
            textAnchor="middle"
            fontFamily="JF Flat"
          >
            {d.date}
          </SvgText>
        ))}
      </Svg>

      {/* Custom Legend */}
      <View style={trendChartStyles.legendContainer}>
        <View style={trendChartStyles.legendItem}>
          <Text style={[trendChartStyles.legendText, { color: chartTheme.legendText }]}>{t('kpis.actualValue')}</Text>
          <View style={[trendChartStyles.legendLine, { backgroundColor: chartTheme.actualLine }]} />
        </View>
        <View style={trendChartStyles.legendItem}>
          <Text style={[trendChartStyles.legendText, { color: chartTheme.legendText }]}>{t('kpis.targetValue')}</Text>
          <View style={[trendChartStyles.legendLineDashed, { borderColor: chartTheme.targetLine }]} />
        </View>
      </View>
    </View>
  );
};

// ============================================================
// Trend Summary Component
// ============================================================

interface TrendSummaryProps {
  readings: KPIValue[];
  targetValue: number;
  polarity?: 'positive' | 'negative';
  isPercentage?: boolean;
}

const TrendSummary = ({ readings, targetValue, polarity = 'positive', isPercentage = true }: TrendSummaryProps) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();

  // Calculate trend metrics
  const latestValue = parseFloat(readings[0]?.value || '0');
  const previousValue = parseFloat(readings[1]?.value || String(latestValue));
  const change = latestValue - previousValue;
  const changePercent = previousValue > 0 ? ((change / previousValue) * 100) : 0;

  // For positive polarity: higher is better. For negative: lower is better
  const isImproving = polarity === 'positive' ? change >= 0 : change <= 0;
  const achievementPercent = targetValue > 0 ? ((latestValue / targetValue) * 100) : 0;

  const summaryTheme = {
    background: isDark ? colors.backgroundAlt : '#F8FAFC',
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    success: '#22C55E',
    danger: '#EF4444',
    warning: '#EAB308',
  };

  return (
    <View style={[trendChartStyles.summaryContainer, { backgroundColor: summaryTheme.background }]}>
      <View style={trendChartStyles.summaryRow}>
        {/* Achievement */}
        <View style={trendChartStyles.summaryItem}>
          <Text style={[trendChartStyles.summaryLabel, { color: summaryTheme.textMuted }]}>
            {t('kpis.achievementPercentage')}
          </Text>
          <Text style={[
            trendChartStyles.summaryValue,
            { color: achievementPercent >= 100 ? summaryTheme.success : (achievementPercent >= 80 ? summaryTheme.warning : summaryTheme.danger) }
          ]}>
            %{formatPct(Math.min(achievementPercent, 100))}
          </Text>
        </View>

        {/* Change */}
        <View style={trendChartStyles.summaryItem}>
          <Text style={[trendChartStyles.summaryLabel, { color: summaryTheme.textMuted }]}>
            {t('kpis.change')}
          </Text>
          <View style={trendChartStyles.changeRow}>
            {isImproving ? (
              <TrendingUpIcon size={18} color={summaryTheme.success} />
            ) : (
              <TrendingDownIcon size={18} color={summaryTheme.danger} />
            )}
            <Text style={[
              trendChartStyles.summaryValue,
              { color: isImproving ? summaryTheme.success : summaryTheme.danger }
            ]}>
              %{change >= 0 ? '+' : ''}{changePercent.toFixed(2)}
            </Text>
          </View>
        </View>

        {/* Latest Value */}
        <View style={trendChartStyles.summaryItem}>
          <Text style={[trendChartStyles.summaryLabel, { color: summaryTheme.textMuted }]}>
            {t('kpis.latestReading')}
          </Text>
          <Text style={[trendChartStyles.summaryValue, { color: summaryTheme.textPrimary }]}>
            {isPercentage ? `%${latestValue}` : latestValue}
          </Text>
        </View>
      </View>
    </View>
  );
};

// ============================================================
// Empty Chart State Component
// ============================================================

interface EmptyChartStateProps {
  height?: number;
}

const EmptyChartState = ({ height = 250 }: EmptyChartStateProps) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();

  return (
    <View style={[
      trendChartStyles.emptyChart,
      {
        height,
        backgroundColor: isDark ? colors.backgroundAlt : '#F8FAFC',
      }
    ]}>
      <ChartIcon size={48} color={colors.textMuted} />
      <Text style={[trendChartStyles.emptyTitle, { color: colors.textPrimary }]}>
        {t('kpis.noReadings')}
      </Text>
      <Text style={[trendChartStyles.emptyText, { color: colors.textMuted }]}>
        {t('kpis.addReadingsToShowTrend')}
      </Text>
    </View>
  );
};

// ============================================================
// Trend Chart Styles
// ============================================================

const trendChartStyles = StyleSheet.create({
  legendContainer: {
    flexDirection: 'row-reverse',
    justifyContent: 'center',
    gap: 24,
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  legendItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
  },
  legendLine: {
    width: 20,
    height: 3,
    borderRadius: 2,
  },
  legendLineDashed: {
    width: 20,
    height: 0,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderRadius: 2,
  },
  legendText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
  summaryContainer: {
    borderRadius: 12,
    padding: 16,
    marginTop: 12,
  },
  summaryRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-around',
  },
  summaryItem: {
    alignItems: 'center',
    gap: 4,
  },
  summaryLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
  summaryValue: {
    fontSize: 18,
    fontFamily: FONTS.numbers,
    fontWeight: 'bold',
  },
  changeRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 4,
  },
  emptyChart: {
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
  },
  emptyTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
  },
});

// ============================================================
// Legacy Line Chart Component (for backwards compatibility)
// ============================================================

interface LegacyLineChartProps {
  data: { date: string; value: number; target: number }[];
  width?: number;
  height?: number;
}

const LineChart = ({ data, width = 280, height = 180 }: LegacyLineChartProps) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();

  // Theme colors for chart
  const chartTheme = {
    gridLine: isDark ? '#374151' : '#E5E7EB',
    axisLabel: colors.textMuted,
    legendText: colors.textMuted,
    emptyText: colors.textMuted,
  };

  if (!data || data.length === 0) {
    return (
      <View style={{ width, height, justifyContent: 'center', alignItems: 'center' }}>
        <Text style={{ color: chartTheme.emptyText, fontSize: 14 }}>{t('common.noSufficientData')}</Text>
      </View>
    );
  }

  const padding = { top: 20, right: 20, bottom: 40, left: 40 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const values = data.map(d => d.value);
  const targets = data.map(d => d.target);
  const allValues = [...values, ...targets];
  const maxValue = Math.max(...allValues, 1);
  const minValue = Math.min(...allValues, 0);
  const valueRange = maxValue - minValue || 1;

  const getX = (index: number) => padding.left + (index / (data.length - 1 || 1)) * chartWidth;
  const getY = (value: number) => padding.top + chartHeight - ((value - minValue) / valueRange) * chartHeight;

  // Create path for actual values
  const actualPath = data.map((d, i) => {
    const x = getX(i);
    const y = getY(d.value);
    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).join(' ');

  // Create path for target line
  const targetPath = data.map((d, i) => {
    const x = getX(i);
    const y = getY(d.target);
    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).join(' ');

  return (
    <View style={{ width, height }}>
      <Svg width={width} height={height}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
          const y = padding.top + chartHeight * (1 - ratio);
          const value = Math.round(minValue + valueRange * ratio);
          return (
            <G key={i}>
              <Path
                d={`M ${padding.left} ${y} L ${width - padding.right} ${y}`}
                stroke={chartTheme.gridLine}
                strokeWidth={1}
                strokeDasharray="4,4"
              />
              <SvgText
                x={padding.left - 8}
                y={y + 4}
                fontSize={10}
                fill={chartTheme.axisLabel}
                textAnchor="end"
              >
                {value}
              </SvgText>
            </G>
          );
        })}

        {/* Target line (dashed) */}
        <Path
          d={targetPath}
          stroke="#9CA3AF"
          strokeWidth={2}
          strokeDasharray="6,4"
          fill="none"
        />

        {/* Actual values line */}
        <Path
          d={actualPath}
          stroke="#3B82F6"
          strokeWidth={2.5}
          fill="none"
        />

        {/* Data points */}
        {data.map((d, i) => (
          <Circle
            key={i}
            cx={getX(i)}
            cy={getY(d.value)}
            r={4}
            fill="#3B82F6"
          />
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => (
          <SvgText
            key={i}
            x={getX(i)}
            y={height - 10}
            fontSize={10}
            fill={chartTheme.axisLabel}
            textAnchor="middle"
          >
            {d.date}
          </SvgText>
        ))}
      </Svg>

      {/* Legend */}
      <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 20, marginTop: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: '#3B82F6' }} />
          <Text style={{ fontSize: 12, color: chartTheme.legendText }}>{t('kpis.actualValue')}</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <View style={{ width: 12, height: 3, backgroundColor: '#9CA3AF' }} />
          <Text style={{ fontSize: 12, color: chartTheme.legendText }}>{t('kpis.targetValue')}</Text>
        </View>
      </View>
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function KPIDetailScreen() {
  const route = useRoute<RouteProps>();
  const navigation = useNavigation<NavigationProp>();
  const { id } = route.params;
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  const { navigateToHome, isViewOnly, canComment } = useRoleNavigation();
  const { isMobile, isTablet, isDesktop, isSmallMobile, width, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { colors, isDark } = useTheme();
  const { translate, translateOrg } = useAutoTranslate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
  };

  // Check if user is strategy_team (they can't add readings)
  const isStrategyTeam = (user?.permissions?.includes('hrcpms.strategic_goals.create') ?? false) || (user?.is_superuser ?? false);
  // DTS 2.2 "إضافة المؤشرات التشغيلية للعنصر": Section Head =
  // إنشاء وقراءة وتعديل (no delete). Deleting KPI definitions is a
  // soft-delete operation reserved for Strategy Team via hrcpms.soft_delete.
  // The previous `!isViewOnly` gate exposed Delete to Section Head — a
  // confusing affordance because the creator could nuke their own
  // indicator without going through the approval flow.
  const canSoftDelete = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.soft_delete')
  );

  // Responsive breakpoints - using hook values
  const isSmallScreen = isMobile;
  const isMediumScreen = isTablet;

  const { data: kpi, setData: setKpi, loading, setLoading } = useCachedData<KPI>(`kpi-${id}`);
  const [readings, setReadings] = useState<KPIValue[]>([]);
  const [evidenceMap, setEvidenceMap] = useState<Record<number, Evidence[]>>({});
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Comments state
  const [apiComments, setApiComments] = useState<ApiKPIComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [editingComment, setEditingComment] = useState<CommentData | null>(null);

  // Reading delete state
  const [deleteReadingId, setDeleteReadingId] = useState<number | null>(null);
  const [showDeleteReadingModal, setShowDeleteReadingModal] = useState(false);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isKpiDeleting, setIsKpiDeleting] = useState(false);
  const isOrgEmployee = user?.position_type === 'EMPLOYEE';
  // The KPI detail response carries `can_enter_reading` — a computed
  // flag the serializer derives by running the exact same Gap 1 /
  // Gap 5 helpers DataEntryPermission uses for the write path. So the
  // button visibility now tracks the API gate 1:1:
  //   * Aggregate strategic KPI → no manual entry (computed value).
  //   * Independent strategic KPI → only implementing_section.
  //   * Operational KPI → section head of owning section, OR an
  //     employee in initiative.assigned_to_employee_ids.
  // Previously this gated on position_type alone — every EMPLOYEE saw
  // the button on every KPI and bounced off 403 when the assignment
  // wasn't theirs.
  const canEnterReading = !!(kpi as any)?.can_enter_reading;

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Convert API comment to CommentData format for display
  const convertApiComment = (apiComment: ApiKPIComment): CommentData => {
    // Get the actual author name from author_details
    const fullName = apiComment.author_details?.full_name || apiComment.author_details?.username || t('common.user');
    const nameParts = fullName.split(' ').filter(Boolean);
    const authorName = nameParts[0] || t('common.user'); // First name only

    // Generate initials from the actual name
    const initials = nameParts.length >= 2
      ? `${nameParts[0][0]} ${nameParts[1][0]}`
      : fullName.slice(0, 2);

    const isOwner = user?.id === apiComment.author;

    // Colors based on author type
    const colorMap: Record<string, string> = {
      'strategy_team': '#A6813F',
      'org_employee': '#9467BD',
    };
    const avatarColor = colorMap[apiComment.author_type] || '#3B82F6';

    return {
      id: apiComment.id,
      name: authorName,
      initials,
      date: formatCommentDate(apiComment.created_at),
      text: apiComment.content,
      avatarColor,
      showMenu: isOwner, // Only show menu for own comments
      isEdited: apiComment.is_edited,
      originalText: apiComment.original_content,
      userType: apiComment.author_type as 'strategy_team' | 'org_employee' | 'superuser' | 'executive',
    };
  };

  // Convert API comments to display format
  const comments = apiComments.map(convertApiComment);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [kpiData, readingsData, commentsData] = await Promise.all([
        kpisApi.get(id),
        kpisApi.getReadings(id),
        kpiCommentsApi.getForKPI(id),
      ]);
      setKpi(kpiData);
      setReadings(readingsData || []);
      setApiComments(commentsData || []);

      // Fetch evidence for all readings
      if (readingsData && readingsData.length > 0) {
        const evidencePromises = readingsData.map((reading: KPIValue) =>
          evidenceApi.forObject('kpi_value', reading.id).catch(() => [])
        );
        const evidenceResults = await Promise.all(evidencePromises);
        const newEvidenceMap: Record<number, Evidence[]> = {};
        readingsData.forEach((reading: KPIValue, index: number) => {
          newEvidenceMap[reading.id] = evidenceResults[index] || [];
        });
        setEvidenceMap(newEvidenceMap);
      }
    } catch (err: any) {
      setError(err.message || t('kpis.loadFailed'));
      showError(t('kpis.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id]);

  // Fetch comments separately for refreshing
  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const commentsData = await kpiCommentsApi.getForKPI(id);
      setApiComments(commentsData || []);
    } catch (err: any) {
      showError(t('comments.loadFailed'));
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  // Comment handlers - integrated with API
  const handleAddComment = async (text: string) => {
    try {
      await kpiCommentsApi.create({
        kpi: id,
        content: text,
        comment_type: 'note',
      });
      showSuccess(t('comments.addSuccess'));
      fetchComments();
      setShowAddComment(false);
    } catch (err: any) {
      console.error('Error creating comment:', err);
      showError(err.response?.data?.detail || err.response?.data?.content?.[0] || t('comments.addFailed'));
    }
  };

  const handleEditComment = (comment: CommentData) => {
    setEditingComment(comment);
    setShowAddComment(true);
  };

  const handleUpdateComment = async (text: string) => {
    if (editingComment) {
      try {
        await kpiCommentsApi.update(editingComment.id, { content: text });
        showSuccess(t('comments.updateSuccess'));
        fetchComments();
      } catch (err: any) {
        console.error('Error updating comment:', err);
        showError(err.response?.data?.detail || t('comments.updateFailed'));
      } finally {
        setEditingComment(null);
        setShowAddComment(false);
      }
    }
  };

  const handleDeleteKpi = () => { setShowDeletionImpact(true); };
  const confirmDeleteKpi = async () => {
    try {
      setIsKpiDeleting(true);
      await kpisApi.delete(id);
      showSuccess(t('kpis.deleteSuccess'));
      navigation.goBack();
    } catch (error: any) {
      showError(t('kpis.deleteFailed'));
    } finally { setIsKpiDeleting(false); setShowDeletionImpact(false); }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await kpiCommentsApi.delete(commentId);
      showSuccess(t('comments.deleteSuccess'));
      fetchComments();
    } catch (err: any) {
      console.error('Error deleting comment:', err);
      const errorMessage = err.response?.data?.detail
        || err.response?.data?.error
        || err.response?.data?.message
        || err.message
        || t('comments.deleteFailed');
      showError(errorMessage);
    }
  };

  const handleDeleteReading = async () => {
    if (!deleteReadingId) return;
    try {
      await kpiValuesApi.delete(deleteReadingId);
      showSuccess(t('kpis.readingDeleteSuccess'));
      setShowDeleteReadingModal(false);
      setDeleteReadingId(null);
      fetchData();
    } catch (err: any) {
      console.error('Error deleting reading:', err);
      showError(err.response?.data?.detail || t('kpis.readingDeleteFailed'));
      setShowDeleteReadingModal(false);
      setDeleteReadingId(null);
    }
  };

  const handleCancelComment = () => {
    setShowAddComment(false);
    setEditingComment(null);
  };

  // Refetch data when screen comes into focus (e.g., after adding a reading)
  useFocusEffect(
    useCallback(() => {
      fetchData();
    }, [fetchData])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  // Calculate statistics
  const calculateStats = () => {
    if (!readings || readings.length === 0) {
      return {
        count: 0,
        average: 0,
        lastValue: null,
        lastDate: null,
        achievementPercentage: 0,
      };
    }

    const values = readings.map(r => parseFloat(r.value) || 0);
    const sum = values.reduce((a, b) => a + b, 0);
    const avg = sum / values.length;
    const lastReading = readings[0];

    return {
      count: readings.length,
      average: parseFloat(avg.toFixed(2)),
      lastValue: lastReading?.value || null,
      lastDate: lastReading?.created_at ? lastReading.created_at.split('T')[0] : null,
      achievementPercentage: lastReading?.achievement_percentage || 0,
    };
  };

  const stats = calculateStats();

  // Prepare chart data with Arabic date formatting
  const getChartData = () => {
    if (!readings || readings.length === 0) return [];

    const targetValue = parseFloat(kpi?.target_value || '0');
    return readings.slice(0, 8).reverse().map(r => ({
      date: r.measurement_date ? formatArabicDate(r.measurement_date) : '',
      value: parseFloat(r.value) || 0,
      target: targetValue,
    }));
  };

  // Get target value for the chart
  const targetValue = parseFloat(kpi?.target_value || '0');

  // Determine polarity for trend summary
  const getPolarity = (): 'positive' | 'negative' => {
    return kpi?.polarity === 'negative' ? 'negative' : 'positive';
  };

  // Handle Excel export
  const handleExportExcel = () => {
    if (!readings || readings.length === 0) {
      showError(t('kpis.noReadingsToExport'));
      return;
    }
    try {
      const exportData = readings.map(r => ({
        measurement_date: r.measurement_date,
        value: r.value,
        target_value: kpi?.target_value || '',
        achievement_percentage: r.achievement_percentage || '',
        notes: r.notes || '',
        created_at: r.created_at?.split('T')[0] || '',
      }));
      exportKPIReadingsToExcel(exportData, kpi?.name || t('kpis.kpi'));
      showSuccess(t('kpis.exportSuccess', { count: readings.length }));
    } catch (error) {
      console.error('Export error:', error);
      showError(t('kpis.exportError'));
    }
  };

  // Pagination
  const totalPages = Math.ceil(readings.length / itemsPerPage);
  const paginatedReadings = readings.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return <LoadingScreen />;
  }

  if (error || !kpi) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={t('common.error')}
        message={error || t('kpis.kpiNotFound')}
        actionLabel={t('common.retry')}
        onAction={fetchData}
      />
    );
  }

  return (
    <PageContainer>
      {/* Unified Navbar */}
      <Navbar />

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.content,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Breadcrumb - dynamic based on KPI's parent entity */}
        <Breadcrumb
          items={(() => {
            const crumbs: { label: string; route?: string; params?: object }[] = [
              { label: t('nav.home') },
            ];
            if (kpi?.strategic_goal) {
              crumbs.push({ label: t('goals.strategicGoals'), route: 'Goals' });
              crumbs.push({ label: t('goals.goalDetails'), route: 'GoalDetail', params: { id: kpi.strategic_goal } });
            } else if (kpi?.initiative) {
              crumbs.push({ label: t('initiatives.title'), route: 'Initiatives' });
              crumbs.push({ label: t('initiatives.initiativeDetails'), route: 'InitiativeDetail', params: { id: kpi.initiative } });
            } else if (kpi?.source_process) {
              crumbs.push({ label: t('processes.title'), route: 'Processes' });
              crumbs.push({ label: t('processes.processDetails'), route: 'ProcessDetail', params: { id: kpi.source_process } });
            } else if (kpi?.source_sub_process) {
              crumbs.push({ label: t('processes.title'), route: 'Processes' });
              crumbs.push({ label: t('processes.subProcessDetails'), route: 'SubProcessDetail', params: { id: kpi.source_sub_process } });
            } else if (kpi?.source_service) {
              crumbs.push({ label: t('services.title'), route: 'Services' });
              crumbs.push({ label: t('services.serviceDetails'), route: 'ServiceDetail', params: { id: kpi.source_service } });
            } else if (kpi?.organizational_unit_goal) {
              crumbs.push({ label: t('unitGoals.title'), route: 'UnitGoals' });
              crumbs.push({ label: t('unitGoals.unitGoalDetails'), route: 'UnitGoalDetail', params: { id: kpi.organizational_unit_goal } });
            } else {
              crumbs.push({ label: t('goals.strategicGoals'), route: 'Goals' });
            }
            crumbs.push({ label: t('kpis.kpiDetails') });
            return crumbs;
          })()}
        />

        {/* Title with Add Reading Button — the DTS banners used to be
            nested inside this same `flexDirection: row` container, so
            every banner became a sibling competing with the title for
            horizontal space. That squeezed the title down to one
            Arabic word per line on independent KPIs. Banners now live
            *after* the row as full-width blocks, and the title keeps
            its natural width. */}
        <View style={[styles.titleRow, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.titleRowSmall]}>
          {/* Add Reading / Delete Buttons.
              The Add-Reading button is governed SOLELY by the authoritative
              `canEnterReading` (API-computed: implementing_section for an
              independent strategic KPI, or the op writer). It must NOT be
              gated behind `!isStrategyTeam` — the implementing-section user
              (e.g. a Section Head who also holds strategic_goals.create) was
              wrongly being hidden the button even though the API allows it. */}
          {((!isStrategyTeam && canSoftDelete) || (canEnterReading && (kpi as any).data_source_type !== 'aggregate')) && (
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {!isStrategyTeam && canSoftDelete && (
                <TouchableOpacity
                  style={{ paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8, backgroundColor: '#FEE2E2', borderWidth: 1, borderColor: '#FECACA' }}
                  onPress={handleDeleteKpi}
                >
                  <Text style={{ fontSize: 13, color: '#DC2626', fontFamily: FONTS.medium }}>{t('common.delete')}</Text>
                </TouchableOpacity>
              )}
              {/* DTS 4.3 level 4(b): aggregate KPIs are computed from
                  contributing initiatives — no manual reading allowed. */}
              {canEnterReading && (kpi as any).data_source_type !== 'aggregate' && (
                <TouchableOpacity
                  style={styles.addReadingButton}
                  onPress={() => navigation.navigate('AddKPIReading', { kpiId: id })}
                >
                  <Text style={[styles.addReadingText, isSmallScreen && styles.addReadingTextSmall]}>
                    {t('kpis.addNewReading')}
                  </Text>
                  <PlusIcon size={16} color="#FFFFFF" />
                </TouchableOpacity>
              )}
            </View>
          )}
          <Text style={[styles.pageTitle, { color: themeColors.textPrimary }, isSmallScreen && styles.pageTitleSmall]}>{translate(kpi.name)}</Text>
          {isViewOnly && !isOrgEmployee && <ViewOnlyBadge style={{ marginTop: 8 }} />}
        </View>

        {/* DTS 4.3 banners: explain the data source so the user knows where
            the achievement number comes from and whether they're expected
            to enter readings. Rendered outside the title row so they get
            full width and don't fight the title for horizontal space. */}
        {(kpi as any).data_source_type === 'aggregate' && (
          <View style={{
            flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 8,
            marginBottom: 24, padding: 12, borderRadius: 8,
            backgroundColor: '#EFF6FF', borderWidth: 1, borderColor: '#93C5FD',
          }}>
            <Text style={{ flex: 1, fontSize: 13, fontFamily: FONTS.regular, color: '#1E40AF', textAlign: 'right', lineHeight: 20 }}>
              ℹ️ هذا مؤشر <Text style={{ fontFamily: FONTS.medium }}>تجميعي</Text> — قيمته تُحسَب تلقائياً من المبادرات الداعمة وفق الصيغة:
              <Text style={{ fontFamily: FONTS.medium }}> Σ(إنجاز المبادرة × وزنها داخل المؤشر)</Text>.
              لا يمكن إدخال قراءات يدوية له.
            </Text>
          </View>
        )}
        {(kpi as any).data_source_type === 'independent' && (
          <View style={{
            flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 8,
            marginBottom: 24, padding: 12, borderRadius: 8,
            backgroundColor: '#FFFBEB', borderWidth: 1, borderColor: '#FCD34D',
          }}>
            <Text style={{ flex: 1, fontSize: 13, fontFamily: FONTS.regular, color: '#92400E', textAlign: 'right', lineHeight: 20 }}>
              ℹ️ هذا مؤشر <Text style={{ fontFamily: FONTS.medium }}>مستقل</Text> — يُدخَل القيمة يدوياً مع رفع دليل إلزامي.
              المبادرات المرتبطة (إن وجدت) تُعرض كـ"مبادرات داعمة" للسياق فقط ولا تدخل في الحساب.
            </Text>
          </View>
        )}


        {/* Main Content Grid - 3 Cards in Row */}
        <View style={[styles.mainGrid, isSmallScreen && styles.mainGridSmall]}>
          {/* Enhanced Trend Chart Card */}
          <View style={[styles.gridCard, styles.trendChartCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.cardTitle, { color: themeColors.textPrimary }]}>{t('kpis.kpiTrend')}</Text>

            {/* Chart or Empty State */}
            {readings.length === 0 ? (
              <EmptyChartState height={250} />
            ) : (
              <>
                {/* Enhanced Line Chart */}
                <View style={styles.enhancedChartContainer}>
                  <EnhancedLineChart
                    data={getChartData()}
                    width={isSmallScreen ? width - 80 : (isMediumScreen ? 320 : 380)}
                    height={280}
                    targetValue={targetValue}
                    isPercentage={kpi?.unit === 'percentage'}
                  />
                </View>

              </>
            )}
          </View>

          {/* Performance Statistics Card */}
          <View style={[styles.gridCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.cardTitle, { color: themeColors.textPrimary }]}>{t('kpis.performanceStatistics')}</Text>
            <View style={styles.perfStatsContent}>
              <View style={styles.perfStatsRight}>
                <View style={styles.statRow}>
                  <Text style={[styles.statValue, { color: themeColors.textPrimary }]}>{stats.count}</Text>
                  <View style={[styles.statLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                    <Text style={[styles.statLabelText, { color: themeColors.textPrimary }]}>{t('kpis.readingsCount')}</Text>
                  </View>
                </View>
                <View style={styles.statRow}>
                  <Text style={[styles.statValue, { color: themeColors.textPrimary }]}>{kpi.unit === 'percentage' ? `% ${stats.lastValue || '-'}` : `${stats.lastValue || '-'} ${translate(kpi.unit)}`}</Text>
                  <View style={[styles.statLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                    <Text style={[styles.statLabelText, { color: themeColors.textPrimary }]}>{t('kpis.latestReading')}</Text>
                  </View>
                </View>
                <View style={styles.statRow}>
                  <Text style={[styles.statValue, { color: themeColors.textPrimary }]}>{stats.lastDate || '-'}</Text>
                  <View style={[styles.statLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                    <Text style={[styles.statLabelText, { color: themeColors.textPrimary }]}>{t('kpis.latestReadingDate')}</Text>
                  </View>
                </View>
              </View>
              <View style={styles.perfStatsDonutRow}>
                <View style={styles.perfStatsDonut}>
                  {/* Primary KPI reading: show LATEST reading's achievement
                      (immediate feedback) rather than the cumulative
                      stage-based value. The stage-based engine returns 0
                      until the first scheduled period is due, which
                      confused users who'd just entered a value and saw 0%.
                      Fall back to current_achievement when there's no
                      reading yet. */}
                  <DonutChart
                    percentage={parseFloat(
                      Number(
                        (kpi as any)?.latest_reading?.achievement_percentage
                          ?? kpi?.current_achievement
                          ?? 0
                      ).toFixed(2)
                    )}
                    size={136}
                    strokeWidth={16}
                    showStatus={false}
                    showLabel={false}
                  />
                </View>
                <View style={[styles.perfStatsAchievementBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                  <Text style={[styles.statLabelText, { color: themeColors.textPrimary }]}>{t('kpis.achievementPercentage')}</Text>
                </View>
                {/* Cumulative (stage-based) — shown smaller as secondary,
                    so users still know the official progress against the
                    measurement schedule. */}
                {(kpi as any)?.latest_reading?.achievement_percentage != null
                  && kpi?.current_achievement != null
                  && Number(kpi.current_achievement) !== Number((kpi as any).latest_reading.achievement_percentage) ? (
                  <Text style={{ fontSize: 11, color: themeColors.textPrimary, opacity: 0.65, marginTop: 6, textAlign: 'center' }}>
                    إنجاز تراكمي: {Number(kpi.current_achievement).toFixed(2)}%
                  </Text>
                ) : null}
              </View>
            </View>
          </View>

          {/* KPI Details Card */}
          <View style={[styles.gridCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.cardTitle, { color: themeColors.textPrimary }]}>{t('kpis.kpiDetails')}</Text>
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{translate(kpi.responsible_display || kpi.owner) || t('common.notSpecified')}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.responsible')}</Text>
              </View>
            </View>
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{translate(kpi.kpi_type_display)}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.type')}</Text>
              </View>
            </View>
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{translate(kpi.frequency_display)}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.frequency')}</Text>
              </View>
            </View>
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{translate(kpi.unit)}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.measurementUnit')}</Text>
              </View>
            </View>
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{kpi.target_value}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.target')}</Text>
              </View>
            </View>
            {kpi.worst_acceptable_value != null && (
              <View style={styles.detailRow}>
                <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{kpi.worst_acceptable_value}</Text>
                <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                  <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.worstAcceptableValue')}</Text>
                </View>
              </View>
            )}
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{kpi.latest_value ?? '-'}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.latestReading')}</Text>
              </View>
            </View>
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>%{kpi.weight}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.weight')}</Text>
              </View>
            </View>
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{translate(kpi.description) || t('common.notSpecified')}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.kpiDescription')}</Text>
              </View>
            </View>
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{translate(kpi.polarity_display)}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.polarity')}</Text>
              </View>
            </View>
            <View style={styles.detailRow}>
              <Text style={[styles.detailValue, { color: themeColors.textPrimary }]}>{translate(kpi.measurement_formula) || t('common.notSpecified')}</Text>
              <View style={[styles.detailLabelBadge, { backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(51, 65, 85, 0.1)' }]}>
                <Text style={[styles.detailLabelText, { color: themeColors.textPrimary }]}>{t('kpis.measurementFormula')}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* Target & Achievement Card */}
        <View style={{ marginBottom: 16 }}>
          <TargetAchievementCard
            title={translate(kpi.name)}
            achieved={kpi.latest_value ?? 0}
            target={kpi.target_value}
            unit={kpi.unit === 'percentage' ? '%' : translate(kpi.unit === 'count' ? 'عدد' : kpi.unit)}
            achievedColor={
              kpi.polarity === 'positive'
                ? (parseFloat(kpi.latest_value ?? '0') >= parseFloat(kpi.target_value))
                  ? '#2ca02c' // Green - target met
                  : '#EAB308' // Yellow - below target
                : (parseFloat(kpi.latest_value ?? '0') <= parseFloat(kpi.target_value))
                ? '#2ca02c' // Green - target met (negative polarity)
                : '#EF4444' // Red - exceeds target (bad for negative)
            }
            // No onViewDetails since we're already on the detail screen
          />
        </View>

        {/* Bottom Section Grid - Comments (1 col) + Table (2 cols) */}
        <View style={[styles.bottomGrid, isSmallScreen && styles.bottomGridSmall]}>
          {/* Comments Section (1 column) */}
          <View style={[styles.commentsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.commentsCardSmall]}>
            {/* Header row with title and add button */}
            <View style={styles.commentsHeader}>
              {/* Add Comment Button - Show for users who can comment */}
              {canComment && (
                <TouchableOpacity
                  style={styles.commentsAddButton}
                  onPress={() => setShowAddComment(true)}
                >
                  <MessageCirclePlusIcon size={15} color={themeColors.iconDefault} />
                </TouchableOpacity>
              )}
              <View style={styles.commentsHeaderTitle}>
                <Text style={[styles.commentsTitle, { color: themeColors.textPrimary }]}>{t('comments.title')}</Text>
              </View>
            </View>

            {/* Comments list */}
            <View style={styles.commentsListContainer}>
              {commentsLoading ? (
                <ActivityIndicator size="small" color="#A9211F" style={{ paddingVertical: 20 }} />
              ) : comments.length === 0 && !showAddComment ? (
                <Text style={styles.noComments}>{t('comments.noComments')}</Text>
              ) : (
                comments.map((comment) => (
                  <CommentCard
                    key={comment.id}
                    comment={comment}
                    onEdit={handleEditComment}
                    onDelete={handleDeleteComment}
                    isOrgEmployee={user?.position_type === 'EMPLOYEE'}
                  />
                ))
              )}

              {/* Add Comment Input */}
              {showAddComment && (
                <AddCommentInput
                  onSubmit={editingComment ? handleUpdateComment : handleAddComment}
                  onCancel={handleCancelComment}
                  editingComment={editingComment}
                />
              )}
            </View>
          </View>

          {/* Readings Table (2 columns) */}
          <View style={[styles.tableCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, isSmallScreen && styles.tableCardSmall]}>
            <View style={styles.tableHeader}>
              <TouchableOpacity style={[styles.exportButton, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]} onPress={handleExportExcel}>
                <Text style={[styles.exportButtonText, { color: themeColors.textPrimary }]}>{t('common.exportExcel')}</Text>
                <ExportIcon size={13} color={themeColors.iconDefault} />
              </TouchableOpacity>
              <Text style={[styles.tableTitle, { color: themeColors.textPrimary }]}>{t('kpis.readingsLog')}</Text>
            </View>

            {/* Table - with horizontal scroll on mobile */}
            <ScrollView horizontal={isSmallScreen} showsHorizontalScrollIndicator={isSmallScreen}>
              <View style={[styles.table, isSmallScreen && styles.tableSmall]}>
                {/* Table Header */}
                <View style={[styles.tableRow, { borderBottomColor: themeColors.cardBorder }]}>
                  {(!isViewOnly || canEnterReading) && (
                    <View style={[styles.tableCell, { width: 80 }]}>
                      <Text style={[styles.tableHeaderCell, { color: themeColors.textPrimary }]}>{t('common.actions')}</Text>
                    </View>
                  )}
                  <View style={[styles.tableCell, styles.tableCellFlex]}>
                    <Text style={[styles.tableHeaderCell, { color: themeColors.textPrimary }]}>{t('kpis.evidence')}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.tableCellFixed]}>
                    <Text style={[styles.tableHeaderCell, { color: themeColors.textPrimary }]}>{t('kpis.entryDate')}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.tableCellFlex]}>
                    <Text style={[styles.tableHeaderCell, { color: themeColors.textPrimary }]}>{t('common.notes')}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.tableCellFixed]}>
                    <Text style={[styles.tableHeaderCell, { color: themeColors.textPrimary }]}>{t('kpis.achievementPercentage')}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.tableCellFixed]}>
                    <Text style={[styles.tableHeaderCell, { color: themeColors.textPrimary }]}>{t('kpis.target')}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.tableCellFixed]}>
                    <Text style={[styles.tableHeaderCell, { color: themeColors.textPrimary }]}>{t('kpis.value')}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.tableCellFixed]}>
                    <Text style={[styles.tableHeaderCell, { color: themeColors.textPrimary }]}>{t('kpis.measurementDate')}</Text>
                  </View>
                </View>

                {/* Table Body */}
                {paginatedReadings.map((reading) => {
                  const readingEvidence = evidenceMap[reading.id] || [];
                  const fileEvidence = readingEvidence.find(e => e.evidence_type === 'file' && e.file);
                  const textEvidence = readingEvidence.find(e => e.evidence_type === 'text');

                  return (
                  <View key={reading.id} style={[styles.tableRow, { borderBottomColor: themeColors.cardBorder }]}>
                    {(!isViewOnly || canEnterReading) && (
                      <View style={[styles.tableCell, { width: 80, flexDirection: 'row', justifyContent: 'center', gap: 8 }]}>
                        <TouchableOpacity
                          onPress={() => navigation.navigate('AddKPIReading', { kpiId: id, readingId: reading.id })}
                          style={{ padding: 4 }}
                        >
                          <EditIcon size={16} color={themeColors.textPrimary} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={() => {
                            setDeleteReadingId(reading.id);
                            setShowDeleteReadingModal(true);
                          }}
                          style={{ padding: 4 }}
                        >
                          <TrashIcon size={16} color="#EF4444" />
                        </TouchableOpacity>
                      </View>
                    )}
                    <View style={[styles.tableCell, styles.tableCellFlex]}>
                      {fileEvidence && fileEvidence.file ? (
                        <TouchableOpacity onPress={() => {
                          const fileUrl = fileEvidence.file?.startsWith('http')
                            ? fileEvidence.file
                            : `${API_BASE_HOST}${fileEvidence.file}`;
                          Linking.openURL(fileUrl);
                        }}>
                          <Text style={[styles.tableCellLink, { color: themeColors.textPrimary }]}>{translate(fileEvidence.title) || t('kpis.viewFile')}</Text>
                        </TouchableOpacity>
                      ) : textEvidence ? (
                        <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]} numberOfLines={1}>{translate(textEvidence.text_content) || t('kpis.text')}</Text>
                      ) : (
                        <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]}>-</Text>
                      )}
                    </View>
                    <View style={[styles.tableCell, styles.tableCellFixed]}>
                      <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]}>{reading.created_at?.replace('T', ' ').slice(0, 19) || '-'}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.tableCellFlex]}>
                      <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]}>{translate(reading.notes) || '-'}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.tableCellFixed]}>
                      {(() => {
                        const achievePct = parseFloat(reading.achievement_percentage || '0');
                        return (
                          <View style={styles.achievementCell}>
                            <MiniDonutChart percentage={parseFloat(achievePct.toFixed(2))} size={28} strokeWidth={4} />
                            <Text style={[styles.achievementCellText, { color: themeColors.textPrimary }]}>{formatPct(achievePct)} %</Text>
                          </View>
                        );
                      })()}
                    </View>
                    <View style={[styles.tableCell, styles.tableCellFixed]}>
                      <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]}>{kpi.target_value} {translate(kpi.unit)}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.tableCellFixed]}>
                      <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]}>{reading.value} {translate(kpi.unit)}</Text>
                    </View>
                    <View style={[styles.tableCell, styles.tableCellFixed]}>
                      <Text style={[styles.tableCellText, { color: themeColors.textPrimary }]}>{reading.measurement_date}</Text>
                    </View>
                  </View>
                  );
                })}

                {readings.length === 0 && (
                  <View style={styles.noDataRow}>
                    <Text style={[styles.noDataText, { color: themeColors.textMuted }]}>{t('kpis.noRecordedReadings')}</Text>
                  </View>
                )}
              </View>
            </ScrollView>

            {/* Pagination */}
            <View style={[styles.pagination, { flexDirection: rtlRow }, isSmallScreen && styles.paginationSmall]}>
              <View style={[styles.paginationControls, { flexDirection: rtlRow }, isSmallScreen && styles.paginationControlsSmall]}>
                <TouchableOpacity
                  style={[styles.paginationButtonGhost, currentPage === 1 && styles.paginationButtonDisabled]}
                  onPress={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <Text style={[styles.paginationButtonGhostText, { color: themeColors.textPrimary }]}>{t('common.previous')}</Text>
                  <ChevronRightIcon size={13} color={themeColors.textPrimary} />
                </TouchableOpacity>

                <View style={[styles.paginationNumbers, { flexDirection: rtlRow }]}>
                  {(() => {
                    const maxVisible = 5;
                    const tp = totalPages || 1;
                    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    const end = Math.min(tp, start + maxVisible - 1);
                    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                  })().map(page => (
                    <TouchableOpacity
                      key={page}
                      style={[styles.paginationNumber, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }, currentPage === page && styles.paginationNumberActive]}
                      onPress={() => setCurrentPage(page)}
                    >
                      <Text style={[styles.paginationNumberText, { color: themeColors.textPrimary }, currentPage === page && styles.paginationNumberTextActive]}>
                        {page}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                <TouchableOpacity
                  style={[styles.paginationButtonGhost, currentPage === totalPages && styles.paginationButtonDisabled]}
                  onPress={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronLeftIcon size={13} color={themeColors.textPrimary} />
                  <Text style={[styles.paginationButtonGhostText, { color: themeColors.textPrimary }]}>{t('common.next')}</Text>
                </TouchableOpacity>
              </View>

              <Text style={[styles.paginationInfo, { color: themeColors.textMuted }]}>
                {t('pagination.showing', { start: (currentPage - 1) * itemsPerPage + 1, end: Math.min(currentPage * itemsPerPage, readings.length || 0), total: readings.length || 0 })}
              </Text>
            </View>
          </View>
        </View>

        {/* DTS UC-03 step 4 — Contributing / supporting initiatives.
            For aggregate KPIs: drives the calculation (read-only summary).
            For independent KPIs: tracking only (label says "supporting"). */}
        {Array.isArray((kpi as any).initiative_contributions_detail)
          && (kpi as any).initiative_contributions_detail.length > 0 && (
          <View style={[styles.gridCard, { marginTop: 16, backgroundColor: themeColors.card, borderColor: themeColors.cardBorder, padding: 16 }]}>
            <Text style={[styles.cardTitle, { color: themeColors.textPrimary, marginBottom: 12 }]}>
              {(kpi as any).data_source_type === 'aggregate'
                ? 'المبادرات الداعمة'
                : 'مبادرات داعمة (للتتبع فقط — لا تدخل في الحساب)'}
            </Text>
            <View style={{ gap: 8 }}>
              {((kpi as any).initiative_contributions_detail || []).map((row: any) => (
                <View
                  key={row.kpi_initiative_id}
                  style={{
                    flexDirection: 'row-reverse', alignItems: 'center', gap: 12,
                    padding: 12, borderRadius: 8, borderWidth: 1,
                    borderColor: themeColors.cardBorder,
                    backgroundColor: themeColors.card,
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{ fontSize: 14, fontFamily: FONTS.medium, color: themeColors.textPrimary, textAlign: 'right' }}
                      numberOfLines={1}
                    >
                      {row.initiative_title || `مبادرة #${row.initiative_id}`}
                    </Text>
                    <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: themeColors.textMuted, textAlign: 'right', marginTop: 2 }}>
                      الإنجاز الحالي: {row.initiative_achievement != null ? `${Number(row.initiative_achievement).toFixed(2)}%` : '—'}
                    </Text>
                  </View>
                  <View style={{
                    paddingHorizontal: 12, paddingVertical: 6, borderRadius: 6,
                    backgroundColor: (kpi as any).data_source_type === 'aggregate'
                      ? '#EFF6FF'
                      : '#FFFBEB',
                  }}>
                    <Text style={{
                      fontSize: 13, fontFamily: FONTS.medium,
                      color: (kpi as any).data_source_type === 'aggregate'
                        ? '#1E40AF'
                        : '#92400E',
                    }}>
                      وزن {Number(row.weight).toFixed(2)}%
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}
      </ScrollView>
      {/* Delete Reading Confirmation Modal */}
      <Modal visible={showDeleteReadingModal} transparent animationType="fade">
        <Pressable style={styles.modalOverlay} onPress={() => setShowDeleteReadingModal(false)}>
          <View style={[styles.modalContent, { backgroundColor: themeColors.card }]}>
            <Text style={[styles.modalTitle, { color: themeColors.textPrimary }]}>{t('common.confirmDelete')}</Text>
            <Text style={[styles.modalMessage, { color: themeColors.textSecondary }]}>{t('kpis.confirmDeleteReading')}</Text>
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: '#F3F4F6' }]}
                onPress={() => setShowDeleteReadingModal(false)}
              >
                <Text style={[styles.modalButtonText, { color: '#374151' }]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: '#EF4444' }]}
                onPress={handleDeleteReading}
              >
                <Text style={[styles.modalButtonText, { color: '#FFFFFF' }]}>{t('common.delete')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Pressable>
      </Modal>
      <ConfirmModal
        visible={showDeletionImpact}
        onCancel={() => setShowDeletionImpact(false)}
        onConfirm={confirmDeleteKpi}
        title={t('common.delete')}
        message={t('kpis.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  contentSmall: {
    padding: 12,
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 16,
  },
  breadcrumbSmall: {
    flexWrap: 'wrap',
    gap: 4,
  },
  breadcrumbLink: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  // Title Row
  titleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 24,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: 8,
  },
  titleRowSmall: {
    flexDirection: 'column-reverse',
    alignItems: 'stretch',
    gap: 12,
    paddingHorizontal: 12,
  },
  pageTitle: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    color: '#414042',
    textAlign: 'right',
    flex: 1,
  },
  pageTitleSmall: {
    fontSize: 16,
    textAlign: 'right',
  },
  addReadingTextSmall: {
    fontSize: 12,
  },
  addReadingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#A9211F',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
    minHeight: 44,
  },
  addReadingText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#f1f5f9',
  },
  // Main Grid - 3 Cards in Row
  mainGrid: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 24,
  },
  mainGridSmall: {
    flexDirection: 'column',
    gap: 16,
  },
  gridCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: 16,
  },
  trendChartCard: {
    minHeight: 450,
  },
  enhancedChartContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
  },
  // Card (for table and other sections)
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Chart styles
  chartContainer: {
    flex: 1,
    alignItems: 'center',
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
    paddingHorizontal: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  legendText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  // Detail Row styles (Figma matching)
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  detailLabelBadge: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    width: 80,
    alignItems: 'flex-end',
  },
  detailLabelText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  detailValue: {
    flex: 1,
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Stat Row styles (Figma matching)
  statRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  statLabelBadge: {
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    width: 120,
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  statLabelText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  statValue: {
    flex: 1,
    fontSize: 12,
    fontFamily: FONTS.numbers,
    color: '#414042',
    textAlign: 'right',
  },
  perfStatsContent: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 24,
  },
  perfStatsRight: {
    gap: 24,
  },
  perfStatsDonutRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
  },
  perfStatsDonut: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  perfStatsAchievementBadge: {
    width: 120,
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    alignItems: 'flex-end',
    justifyContent: 'center',
    alignSelf: 'stretch',
  },
  achievementRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    flex: 1,
  },
  achievementChart: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  // Legacy stat styles (keep for compatibility)
  statLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
  },
  legacyStatValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#1F2937',
  },
  // Table Header (used in cards)
  tableHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 4,
  },
  // Bottom Grid - Comments (1 col) + Table (2 cols)
  bottomGrid: {
    flexDirection: 'row',
    gap: 20,
  },
  bottomGridSmall: {
    flexDirection: 'column-reverse',
    gap: 16,
  },
  // Comments Card Styles
  commentsCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: 16,
    alignSelf: 'flex-start',
    overflow: 'visible',
  },
  commentsCardSmall: {
    flex: 0,
    alignSelf: 'stretch',
  },
  commentsHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24,
    marginBottom: 16,
  },
  commentsAddButton: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: 'rgba(51, 65, 85, 0.05)',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  commentsHeaderTitle: {
    flex: 1,
    alignItems: 'flex-end',
  },
  commentsTitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  commentsListContainer: {
    gap: 8,
    overflow: 'visible',
  },
  commentCard: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    position: 'relative',
    zIndex: 1,
  },
  commentCardMenuOpen: {
    zIndex: 1000,
  },
  commentHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    gap: 8,
    paddingVertical: 2,
  },
  commentMenuWrapper: {
    position: 'relative',
    zIndex: 100,
  },
  commentMenuButton: {
    padding: 10,
    minWidth: 44,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  commentMenuDropdown: {
    position: 'absolute',
    top: 28,
    left: -8,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.2)',
    elevation: 16,
    minWidth: 160,
    zIndex: 1000,
    paddingVertical: 12,
    paddingHorizontal: 4,
  },
  commentMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  commentMenuItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  commentMenuItemTextDelete: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#EF4444',
  },
  commentMenuSeparator: {
    height: 1,
    backgroundColor: '#E2E8F0',
    marginHorizontal: 8,
  },
  commentAuthorInfo: {
    flex: 1,
    alignItems: 'flex-end',
  },
  commentNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  commentBadgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    marginTop: 2,
  },
  userTypeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  userTypeBadgeText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
  },
  commentAuthorName: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  commentDate: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
  },
  commentAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  commentAvatarText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: '#FCFCFC',
    textAlign: 'center',
  },
  commentSeparatorLine: {
    height: 1,
    backgroundColor: '#E2E8F0',
    marginVertical: 0,
  },
  commentContent: {
    paddingVertical: 0,
  },
  commentText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 20,
  },
  noComments: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'center',
    paddingVertical: 20,
  },
  // Edited Tag styles
  editedTag: {
    backgroundColor: '#FEF3C7',
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  editedTagText: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: '#92400E',
  },
  // Add Comment Input styles
  addCommentCard: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  addCommentHeader: {
    paddingVertical: 2,
    alignItems: 'flex-end',
  },
  addCommentHeaderText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  addCommentSeparator: {
    height: 1,
    backgroundColor: '#E2E8F0',
  },
  addCommentInputContainer: {
    minHeight: 40,
    paddingVertical: 4,
  },
  addCommentInput: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    minHeight: 32,
    padding: 0,
  },
  addCommentActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 2,
  },
  addCommentActionButton: {
    padding: 10,
    minWidth: 44,
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Original Text Modal styles
  originalTextModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  originalTextModalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    width: '95%',
    maxWidth: 400,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
    elevation: 10,
  },
  originalTextModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  originalTextModalTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },
  originalTextModalBody: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  originalTextModalText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    lineHeight: 22,
  },
  // Legacy comment styles (removed)
  commentEditedBadge: {
    fontSize: 10,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    marginTop: 4,
  },
  // Table Card Styles
  tableCard: {
    flex: 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 20,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    gap: 20,
  },
  tableCardSmall: {
    flex: 0,
    padding: 12,
    gap: 12,
  },
  tableTitle: {
    flex: 1,
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
    minHeight: 44,
  },
  exportButtonText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Table styles
  table: {
    borderWidth: 0,
  },
  tableSmall: {
    minWidth: 700,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    alignItems: 'center',
  },
  tableCell: {
    paddingHorizontal: 8,
    paddingVertical: 7.5,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  tableCellFlex: {
    flex: 1,
  },
  tableCellFixed: {
    width: 100,
  },
  tableHeaderCell: {
    fontSize: 14,
    fontFamily: FONTS.bold,
    color: '#414042',
    textAlign: 'right',
  },
  tableCellText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  tableCellLink: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textDecorationLine: 'underline',
    textAlign: 'right',
  },
  achievementCell: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  achievementCellText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'center',
  },
  noDataRow: {
    padding: 40,
    alignItems: 'center',
  },
  noDataText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
  },
  // Pagination Styles (Figma matching)
  pagination: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  paginationSmall: {
    flexDirection: 'column-reverse',
    gap: 12,
    alignItems: 'center',
  },
  paginationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  paginationControlsSmall: {
    gap: 4,
  },
  paginationButtonGhost: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    minHeight: 44,
  },
  paginationButtonGhostText: {
    fontSize: 14,
    fontFamily: FONTS.bold,
    color: '#334155',
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationNumbers: {
    flexDirection: 'row',
    gap: 8,
  },
  paginationNumber: {
    width: 34,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  paginationNumberActive: {
    backgroundColor: '#FFFFFF',
    borderColor: '#CBD5E1',
  },
  paginationNumberText: {
    fontSize: 14,
    fontFamily: FONTS.bold,
    color: '#414042',
  },
  paginationNumberTextActive: {
    color: '#414042',
  },
  paginationInfo: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'left',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: 340,
    borderRadius: 12,
    padding: 24,
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.bold,
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'center',
    marginBottom: 20,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
  },
  modalButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
  },
  modalButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
  },
});
